/** Deterministic random source. Every shape gets its own, seeded per visit. */
export type Rng = () => number;

/** Anything with mutable coordinates the sampling helpers can write into. */
export interface Placeable {
  x: number;
  y: number;
  z: number;
}

/** An immutable 3D point, used for precomputed skeletons. */
export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Three colours per shape. Every particle carries a `tint` in [0, 1] which is
 * interpolated base → shade → accent, so one float per particle buys a whole
 * material palette: rock and snow, concrete and window light, water and foam.
 */
export interface Palette {
  readonly base: number;
  readonly shade: number;
  readonly accent: number;
}

/**
 * A shape instance, prepared for one visit. `place` is called exactly once for
 * every particle index in ascending order, which lets the baker spread the work
 * across frames while any sequential randomness stays deterministic.
 */
export interface PreparedShape {
  /**
   * Position particle `index` by writing to the sink's fields. The baker
   * commits them, so there is no need to call `emit()` — and a call that writes
   * nothing just repeats the particle before it.
   */
  readonly place: (index: number, sink: ShapeSink) => void;
}

export interface ShapeContext {
  readonly random: Rng;
  /** Total particles that will be placed. */
  readonly total: number;
  /** Continuous variation knob in [0, 1): the same form in a different mood. */
  readonly variant: number;
}

/** A recognisable thing the visualiser can become. */
export interface ShapeDefinition {
  readonly name: string;
  readonly palette: Palette;
  /** Idle oscillation speed, radians per second. */
  readonly driftRate: number;
  /** How far the camera should sit back, as a multiple of the form's radius. */
  readonly framing: number;
  readonly prepare: (context: ShapeContext) => PreparedShape;
}

/**
 * Guard against a shape's arithmetic going non-finite. One NaN in a position
 * would poison the form's bounding box, and from there the camera — a visualiser
 * meant to run for hours cannot have a single bad particle end the show. The
 * unit tests assert no shape ever needs this.
 */
function finite(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

/**
 * The write head the shapes talk to. A shape sets the fields it cares about and
 * calls `emit()`; everything unset falls back to a sensible default. Reusing one
 * mutable sink for hundreds of thousands of particles keeps the bake
 * allocation-free.
 */
export class ShapeSink implements Placeable {
  public x = 0;
  public y = 0;
  public z = 0;
  /** Palette coordinate: 0 = base, 0.5 = shade, 1 = accent. */
  public tint = 0;
  /** Idle oscillation vector in world units — the form's living motion. */
  public driftX = 0;
  public driftY = 0;
  public driftZ = 0;
  /** Oscillation phase in radians; tie it to position for coherent waves. */
  public phase = 0;

  private readonly form: Float32Array;
  private readonly motion: Float32Array;
  private readonly capacity: number;
  private cursor = 0;

  constructor(form: Float32Array, motion: Float32Array) {
    this.form = form;
    this.motion = motion;
    this.capacity = Math.floor(form.length / 4);
  }

  /** How many particles have been written since the last `rewind()`. */
  public get written(): number {
    return this.cursor;
  }

  public rewind(): void {
    this.cursor = 0;
  }

  /**
   * Commit the current values as one particle and clear the per-particle extras.
   * Called by the baker once per `place()`; shapes do not call it themselves.
   */
  public emit(): void {
    if (this.cursor >= this.capacity) {
      return;
    }
    const at = this.cursor * 4;
    this.form[at] = finite(this.x);
    this.form[at + 1] = finite(this.y);
    this.form[at + 2] = finite(this.z);
    this.form[at + 3] = finite(this.tint);
    this.motion[at] = finite(this.driftX);
    this.motion[at + 1] = finite(this.driftY);
    this.motion[at + 2] = finite(this.driftZ);
    this.motion[at + 3] = finite(this.phase);
    this.cursor += 1;
    this.driftX = 0;
    this.driftY = 0;
    this.driftZ = 0;
    this.phase = 0;
    this.tint = 0;
  }
}
